import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, concatMap, distinctUntilChanged, EMPTY, filter, finalize, forkJoin, map, of, pipe, startWith } from 'rxjs';
import { MinuteConditionDTO } from 'src/app/features/minute-conditions/interfaces/minute-condition.interface';
import { MinuteConditionsService } from 'src/app/features/minute-conditions/minute-conditions.service';
import { faTrash, faPlus, faTriangleExclamation, faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { UpdateMinuteRequestMinuteConditionDTO } from 'src/app/pages/dashboards/files-clients/sub-page/minutes/interface/minute-request.interface'
import Swal from 'sweetalert2';
import { MinuteRequestService } from 'src/app/pages/dashboards/files-clients/sub-page/minutes/service/minute-request.service';
import { NoDuplicateMinuteConditionsValidator } from './no-duplicate-minute-conditions-validator';
import { formatTextWithNoDiacritics } from './formatting-condition';

@Component({
  selector: 'app-manage-minute-request-conditions-modal',
  templateUrl: './manage-minute-request-conditions-modal.component.html',
  styleUrl: './manage-minute-request-conditions-modal.component.scss'
})
export class ManageMinuteRequestConditionsModalComponent implements OnInit, OnDestroy{

  private autoGeneratedIndex: number = 0;

  public manageConditionForm: FormGroup;

  public allConditions: MinuteConditionDTO[] = [];
  public availableConditions$: BehaviorSubject<MinuteConditionDTO[]>;

  public hasFetchConditionsDone: boolean = false;
  public showButtonToAddNewCondition: boolean = false;

  public isRegisteringNewMinuteCondition: boolean = false;

  public icons = {
    faTrash, faPlus, faTriangleExclamation, faCircleExclamation
  }

  constructor(
    public dialogRef: MatDialogRef<ManageMinuteRequestConditionsModalComponent>,
    private minuteConditionsService: MinuteConditionsService,
    private minuteRequestsService: MinuteRequestService,
    @Inject(MAT_DIALOG_DATA)public data: ManageMinuteRequestConditionModalViewModel,
    private fb: FormBuilder
  ){
    this.availableConditions$ = new BehaviorSubject<MinuteConditionDTO[]>([]);
    this.manageConditionForm = this.fb.group({
      minuteRequestId: [this.data.minuteRequestId],
      conditions: this.fb.array([
        this.createNewCondition()
      ], [Validators.required, NoDuplicateMinuteConditionsValidator])
    });

  }

  ngOnInit(): void {
    this.fetchAvailableConditions();
    // this.trackConditionsGroupsChange();
  }

  public fetchAvailableConditions(){
    this.minuteConditionsService.getAllMinuteConditions()
    .pipe(
      finalize(() => this.hasFetchConditionsDone = true)
    )
    .subscribe({
      next:(allMinuteConditions) => {
        this.allConditions = allMinuteConditions;
        this.updateAvailableConditionsToSelect();
      }
    })
  }

  /**
   * 
   * @param filterString texto que el usuario envia desde el autocompletado
   * @returns numero de condiciones disponibles para seleccionar
   */
  public updateAvailableConditionsToSelect(filterString: string = ''): number {
    const alreadyUsedConditions = this.data.conditions.map(c => c.minuteConditionId);
    const currentSelectedConditions: number[] = this.conditions.controls
    .map((group) => group.get('selectedCondition')?.value?.id)
    .filter((value) => value);
    const newAvailableConditions = this.allConditions.filter(c => ![...alreadyUsedConditions, ...currentSelectedConditions].includes(c.id));
    if(filterString){
      const filteredByUserTyping = newAvailableConditions.filter(c => c.name.toLowerCase().includes(filterString.toLowerCase()))
      this.availableConditions$.next(filteredByUserTyping);
      return filteredByUserTyping.length;
    }
    this.availableConditions$.next(newAvailableConditions);
    return newAvailableConditions.length;
  }

  get conditions() {
    return this.manageConditionForm.get('conditions') as FormArray;
  }

  getConditionAt(idx: number) {
    return this.conditions.at(idx) as FormGroup;
  }

  public getSelectedConditionId(idx: number) : number | null {
    return this.conditions?.at(idx).get('conditionId')?.value || null;
  }

  public getConditionName(idx: number) {
    return this.conditions.at(idx).get('selectedCondition')?.value?.name || 'Seleccione condición especial';
  }
  

  public registerNewMinuteCondition(idx: number){
    this.showButtonToAddNewCondition = false;
    const conditionGroup = this.conditions.at(idx) as FormGroup;

    const { selectedCondition } = conditionGroup?.value;
    if(typeof selectedCondition !== 'string')
      return;

    const newConditon = {
      id: null,
      name: selectedCondition.trim()
    };
    this.conditions.at(idx).patchValue({selectedCondition: newConditon});
  }

  /**
   * 
   * @param name nombre de la nueva condicion
   * @param description descripcion a asignar a la minuta tras crear la condicion
   * @returns Nueva entidad de la condición a agregar a la minuta
   */
  private registerUnexistingMinuteCondition(name: string, description: string | null){
    return this.minuteConditionsService.postMinuteCondition(name)
    .pipe(
      map(res => {
        return <UpdateMinuteRequestMinuteConditionDTO>{
          minuteConditionId: res.value.id,
          name: res.value.name,
          description: description
        }
      })
    );
  }

  onClosedPanel(idx: number) {
    this.showButtonToAddNewCondition = false;
    this.validateContentAfterBlur(idx, 0);
  }

  createNewCondition(): FormGroup
  {
    this.autoGeneratedIndex++;
    const formGroup = this.fb.group({
      index: [this.autoGeneratedIndex],
      selectedCondition: [null, [Validators.required]],
      description: ['']
    });    

    formGroup.get('selectedCondition').valueChanges
    .pipe(
      startWith(''),
      distinctUntilChanged()
    ).subscribe((newCondition: MinuteConditionDTO | string | null) => {
      if(!newCondition) return;
      // Recibir string si el usuario lo esta escribiendo en el autocomplete
      const conditionString = typeof newCondition === 'string' ? newCondition : '';
      this.updateAvailableConditionsToSelect(conditionString);
    })
    return formGroup;
  }

  addNewCondition() {
    this.conditions.push(this.createNewCondition());
  }

  removeCondition(idx: number) {
    this.conditions.removeAt(idx);
    this.updateAvailableConditionsToSelect();
  }

  public submitConditions(){
    if(!this.manageConditionForm.valid) {
      this.manageConditionForm.markAllAsTouched();
      return;
    }
    const {minuteRequestId, conditions } = this.manageConditionForm.value;

    const newMinuteConditionsWithUnexistingCondition : any[] = conditions.filter(c => !c.selectedCondition.id);
    const registerNewMinuteConditions$ = newMinuteConditionsWithUnexistingCondition.length > 0 
    ? forkJoin(newMinuteConditionsWithUnexistingCondition.map((c) => this.registerUnexistingMinuteCondition(c.selectedCondition.name, c.description)))
    : of([]);

    const conditionsToRegisterToMinute: UpdateMinuteRequestMinuteConditionDTO[] =
      conditions
      .filter(c => c.selectedCondition.id)
      .map(c => <UpdateMinuteRequestMinuteConditionDTO>{
        minuteConditionId: c.selectedCondition.id,
        description: c.description
    });

    Swal.fire({
      title: 'Registrando nuevas condiciones',
      heightAuto: false,
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => Swal.showLoading()
    });

    const finalOperation$ = registerNewMinuteConditions$
    .pipe(
      concatMap((registerNeWConditions)=> {
        return this.minuteRequestsService.updateMinuteRequestMinuteConditions(minuteRequestId, [...conditionsToRegisterToMinute, ...registerNeWConditions])
      })
    );

    finalOperation$.subscribe({
      next:(res) => this.closeSucess(res.resultMessage),
      error: (err) => this.showErrorPopup(err.error.resultMessage||'Ha ocurrido un error inesperado')
    });
  }

  private showErrorPopup(message: string) {
    Swal.fire({
      titleText: message,
      heightAuto: false,
      icon: 'error',
    });
  }

  private closeSucess(message: string) {
    Swal.fire({
      titleText: message,
      icon: 'success',
      heightAuto: false,
      willClose: () => {
        this.dialogRef.close();
      }
    })
  }

  trackByKey(index: number, item: AbstractControl): string | number {
    return item.value?.index; // Retorna un valor único para identificar cada elemento.
  }
  
  public onBlurAutoComplete(event: FocusEvent,idx:number){
    const relatedTarget = event.relatedTarget as HTMLElement;
    if(relatedTarget && relatedTarget.classList.contains('removal-btn')){
      return;
    }
    this.validateContentAfterBlur(idx, 500)
  }

  private validateContentAfterBlur(idx: number, msDelay: number = 0){
    setTimeout(() => {
      const conditionGroup = this.conditions.at(idx) as FormGroup;
      const { selectedCondition } = conditionGroup.value;
  
      if( typeof(selectedCondition) === 'string'){
        if(!selectedCondition) {
          this.conditions.at(idx).patchValue({selectedCondition: null});
          return;
        }
        const foundCondition = this.searchConditionWihInsertedText(selectedCondition);
        this.conditions.at(idx).patchValue({selectedCondition: foundCondition || {id: null, name: selectedCondition}});
      }  
    }, msDelay)
  }

  private searchConditionWihInsertedText(inputText: string): MinuteConditionDTO | null {
    const formattedInput = formatTextWithNoDiacritics(inputText);
    const condition = this.allConditions.find(c => formatTextWithNoDiacritics(c.name) === formattedInput);
    return condition;
  }

  displayConditionWith(condition: MinuteConditionDTO) {
    return condition && condition.name ? condition.name : '';
  }

  ngOnDestroy(): void {
    this.availableConditions$.complete();
  }

}

export interface ManageMinuteRequestConditionModalViewModel {
  minuteRequestId: number;
  conditions: MinuteRequestConditionViewModel[];
}

export interface MinuteRequestConditionViewModel {
  minuteRequestMinuteConditionId: bigint;
  minuteConditionId: number;
  description: string | null;
}